import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contextos/autenticacion/ContextoAutenticacion'
import { esErrorApiAplicacion } from '../../services/apiError'
import { UsuarioLogin } from '../../types'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { GoogleLogin } from '@react-oauth/google'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowLeft,
  AlertCircle,
  Loader2,
  GraduationCap,
  Layers
} from 'lucide-react'
import { IsotipoLogixPort } from '../../componentes/ui/LogoLogixPort'

export const PaginaLogin: React.FC = () => {
  const { login, loginGoogle, usuario, isLoading } = useAuth()
  
  const schema = z.object({
    email: z.string({ required_error: 'El correo electrónico es requerido' }).email('Ingresa un correo electrónico válido'),
    password: z.string({ required_error: 'La contraseña es requerida' }).min(6, 'La contraseña debe tener al menos 6 caracteres'),
    recordarme: z.boolean().optional().default(false),
  })

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<UsuarioLogin>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      recordarme: false,
    },
  })
  
  const [showPassword, setShowPassword] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [generalError, setGeneralError] = useState<string | null>(null)

  useEffect(() => {
    setIsCheckingAuth(false)
  }, [usuario, isLoading])

  const onSubmit = async (data: UsuarioLogin) => {
    try {
      await login(data)
    } catch (error: unknown) {
      const msg =
        esErrorApiAplicacion(error) && error.code === 'validation_error'
          ? 'Verifica los datos capturados e inténtalo nuevamente.'
          : error instanceof Error
            ? error.message
            : 'Correo o contraseña incorrectos'
      setGeneralError(msg)
    }
  }

  const togglePassword = () => {
    setShowPassword(!showPassword)
  }

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#030303] flex justify-center items-center">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-[#030303] dark:text-zinc-100 relative flex items-center justify-center overflow-hidden selection:bg-cyan-500/30">
      
      {/* Background Effects (Matching Landing Page) */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)]" />
        <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[40%] md:w-[40%] md:h-[40%] rounded-full bg-cyan-500/20 blur-[100px] md:blur-[120px] mix-blend-screen dark:bg-cyan-500/10" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[40%] md:w-[40%] md:h-[40%] rounded-full bg-violet-500/20 blur-[100px] md:blur-[120px] mix-blend-screen dark:bg-violet-500/10" />
      </div>

      {/* Header flotante minimalista */}
      <header className="absolute top-6 left-0 right-0 z-50 w-full max-w-7xl mx-auto flex justify-between px-6 md:px-12">
        <Link 
          to="/" 
          className="group flex items-center gap-2 rounded-full border border-slate-200/50 bg-white/70 px-4 py-2 backdrop-blur-xl shadow-sm dark:border-white/10 dark:bg-black/50 transition-all hover:bg-white dark:hover:bg-black"
        >
          <ArrowLeft className="h-4 w-4 text-slate-500 transition-transform group-hover:-translate-x-1 dark:text-zinc-400" />
          <span className="text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
            Regresar
          </span>
        </Link>
      </header>

      {/* Main Content Centrado */}
      <div className="relative z-10 w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 px-6 md:px-12 py-24">
        
        {/* Panel Izquierdo (Hero Auth Bento) */}
        <div className="hidden lg:flex flex-col justify-center">
          <Link to="/" className="flex items-center gap-3 mb-8 group">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-tr from-slate-950 via-slate-900 to-blue-950 p-1.5 shadow-lg shadow-cyan-500/10 border border-slate-800/80 group-hover:scale-105 transition-all duration-300 dark:from-white/10 dark:via-white/5 dark:to-cyan-500/10 dark:border-white/15">
              <IsotipoLogixPort sizeCls="w-full h-full" />
            </div>
            <div>
              <span className="text-2xl font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
                Logix<span className="text-cyan-600 dark:text-cyan-400">Port</span>
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20">v1.0</span>
              </span>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-zinc-500">
                Logistics & Digital Twin OS
              </p>
            </div>
          </Link>

          <div className="max-w-md space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                CAMPUS EDITION · PRODUCCIÓN ACTIVA
              </div>

              <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.08] mb-4">
                El centro de comando logístico y simulación 3D.
              </h1>
              <p className="text-sm lg:text-base text-slate-600 dark:text-zinc-400 leading-relaxed font-normal">
                Accede al almacén de prácticas tridimensional, trazabilidad de pallets GS1-128, clasificación arancelaria LIGIE y dictámenes de comercio exterior.
              </p>
            </motion.div>

            {/* Mini Bento Badges de Confianza */}
            <div className="grid gap-3 pt-2">
              <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-white/60 dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                <div className="h-9 w-9 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                  <GraduationCap className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900 dark:text-white">Alumnos y Docentes</p>
                  <p className="text-[11px] text-slate-500 dark:text-zinc-400">Acceso a laboratorios de contingencia y consola de prácticas.</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-white/60 dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                <div className="h-9 w-9 rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-center justify-center shrink-0">
                  <Layers className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900 dark:text-white">Gemelo Digital 3D</p>
                  <p className="text-[11px] text-slate-500 dark:text-zinc-400">Navegación espacial interactiva con cálculo estructural en toneladas.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Panel Derecho (Formulario) */}
        <div className="flex items-center justify-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md"
          >
            {/* Tarjeta Glassmorphism */}
            <div className="rounded-[2rem] border border-slate-200/80 bg-white/80 p-8 sm:p-10 shadow-2xl backdrop-blur-2xl dark:border-white/10 dark:bg-[#0c0c0c]/85">
              
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                  ACCESO A CAMPUS EDITION
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-1.5">
                  Iniciar Sesión
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-zinc-400">
                  Ingresa con tu correo institucional asignado
                </p>
              </div>

              {/* Guía Rápida para Alumnos */}
              <div className="mb-6 rounded-2xl border border-blue-500/20 bg-blue-500/5 dark:bg-blue-500/10 p-3 text-xs text-slate-600 dark:text-zinc-300 flex items-start gap-2.5">
                <GraduationCap className="h-4 w-4 shrink-0 text-blue-600 dark:text-cyan-400 mt-0.5" />
                <p className="leading-snug text-[11px]">
                  <strong className="text-slate-900 dark:text-white">¿Eres estudiante?</strong> Utiliza el correo institucional y contraseña que te proporcionó tu profesor.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                
                {/* Campo Correo */}
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      {...register('email')}
                      className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                        errors.email ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                      }`}
                      placeholder="tu@email.com"
                    />
                  </div>
                  {errors.email?.message && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Campo Contraseña */}
                <div className="space-y-1.5">
                  <label htmlFor="password" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                    Contraseña
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                    </div>
                    <input 
                      type={showPassword ? 'text' : 'password'}
                      id="password" 
                      {...register('password')}
                      className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-11 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                        errors.password ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                      }`}
                      placeholder="••••••••"
                      disabled={isSubmitting}
                    />
                    <button 
                      type="button" 
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-cyan-500 dark:text-zinc-500 dark:hover:text-cyan-400 transition-colors"
                      onClick={togglePassword}
                      disabled={isSubmitting}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password?.message && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Recordarme y Recuperar */}
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="recordarme"
                      {...register('recordarme')}
                      className="h-4 w-4 rounded border-slate-300 text-cyan-500 focus:ring-cyan-500 dark:border-white/10 dark:bg-black/50 dark:checked:bg-cyan-500"
                    />
                    <label htmlFor="recordarme" className="text-xs font-medium text-slate-600 dark:text-zinc-400 cursor-pointer">
                      Recordarme
                    </label>
                  </div>
                  <Link to="/forgot-password" className="text-xs font-bold text-cyan-600 hover:text-cyan-500 dark:text-cyan-400 transition-colors">
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>

                {/* Botón Iniciar Sesión */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-all hover:scale-[1.02] hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.6)] disabled:opacity-70 disabled:hover:scale-100"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Autenticando...</span>
                    </>
                  ) : (
                    <span>Entrar al sistema</span>
                  )}
                </button>

                {/* Error General */}
                <AnimatePresence>
                  {generalError && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-center text-xs font-medium text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        {generalError}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Divider */}
                <div className="relative flex items-center justify-center py-4">
                  <div className="w-full border-t border-slate-200/50 dark:border-white/10" />
                  <span className="absolute bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:bg-[#0a0a0a] dark:text-zinc-500">
                    O ENTRA CON
                  </span>
                </div>

                {/* Google Login */}
                {/* Google Login */}
                <div className="flex justify-center mb-4 relative z-50 transform-gpu" style={{ WebkitTransform: 'translateZ(0)', transform: 'translateZ(0)' }}>
                  <div className="w-full max-w-[300px] hover:opacity-90 active:scale-95 transition-all">
                    <GoogleLogin
                      onSuccess={async (credentialResponse) => {
                        if (credentialResponse.credential) {
                          try {
                            await loginGoogle(credentialResponse.credential)
                          } catch (error: unknown) {
                            setGeneralError(error instanceof Error ? error.message : 'Error de Google Auth')
                          }
                        }
                      }}
                      onError={() => {
                        setGeneralError('Fallo al iniciar sesión con Google')
                      }}
                      theme="outline"
                      shape="pill"
                      text="continue_with"
                      width={300}
                    />
                  </div>
                </div>

                {/* Divider 2 */}
                <div className="relative flex items-center justify-center py-4">
                  <div className="w-full border-t border-slate-200/50 dark:border-white/10" />
                  <span className="absolute bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:bg-[#0a0a0a] dark:text-zinc-500">
                    ¿NUEVO AQUÍ?
                  </span>
                </div>

                {/* Registro */}
                <div className="text-center">
                  <Link
                    to="/register"
                    className="text-sm font-medium text-slate-600 dark:text-zinc-400 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors"
                  >
                    Crea una cuenta en <span className="font-bold">LogiXport</span>
                  </Link>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default PaginaLogin
